import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShoppingCart, Zap, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatINR, track } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";

// A single pack variant card
export default function ProductCard({ product, variant }) {
  const { addItem, setOpen } = useCart();
  const [qty, setQty] = useState(1);
  const navigate = useNavigate();

  const discount = variant.mrp > variant.price ? Math.round(((variant.mrp - variant.price) / variant.mrp) * 100) : 0;
  const out = (variant.stock ?? 0) <= 0;
  const low = !out && variant.stock <= (variant.low_stock_threshold ?? 0);
  const popular = variant.popular;

  const add = () => {
    addItem(product, variant, qty);
    track("add_to_cart", { sku: variant.sku, qty });
    toast.success(`Added ${qty} × ${product.name} ${variant.pack_size}`);
  };
  const buyNow = () => {
    addItem(product, variant, qty);
    track("add_to_cart", { sku: variant.sku, qty, buy_now: true });
    navigate("/checkout");
  };

  const idKey = variant.pack_size.replace(/\s/g, "").toLowerCase();

  return (
    <div
      data-testid={`pack-card-${idKey}`}
      className={`relative rounded-2xl p-5 sm:p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
        popular
          ? "bg-gradient-to-b from-wheat-pale to-white border-2 border-wheat/50 shadow-xl"
          : "bg-white border border-forest/10 shadow-[0_4px_20px_rgba(18,55,42,0.06)] hover:shadow-[0_8px_30px_rgba(18,55,42,0.12)]"
      }`}
    >
      {popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-forest text-wheat text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
          Most Popular
        </span>
      )}
      <div className="relative overflow-hidden rounded-xl bg-sand aspect-square mb-4">
        <img src={(product.images || [])[0]} alt={`${product.name} ${variant.pack_size}`} className="h-full w-full object-cover" loading="lazy" />
        {discount > 0 && (
          <span className="absolute top-2 right-2 bg-discount text-white text-xs font-bold px-2 py-1 rounded-md" data-testid={`discount-${idKey}`}>
            {discount}% OFF
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl font-bold text-forest-dark">{variant.pack_size}</h3>
        <span className={`text-xs font-semibold ${out ? "text-discount" : low ? "text-terracotta" : "text-forest"}`} data-testid={`stock-${idKey}`}>
          {out ? "Out of stock" : low ? "Low stock" : "In stock"}
        </span>
      </div>
      <p className="text-xs text-charcoal/55 mb-3">{variant.ideal_for}</p>

      <div className="flex items-baseline gap-2 mb-4">
        <span className="text-2xl font-extrabold text-forest-dark" data-testid={`price-${idKey}`}>{formatINR(variant.price)}</span>
        {discount > 0 && <span className="text-sm text-charcoal/40 line-through">{formatINR(variant.mrp)}</span>}
      </div>

      <div className="mt-auto space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-sm text-charcoal/70">Quantity</span>
          <div className="flex items-center border border-forest/20 rounded-full">
            <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2" data-testid={`qty-dec-${idKey}`} disabled={out}><Minus size={14} /></button>
            <span className="w-8 text-center font-semibold" data-testid={`qty-${idKey}`}>{qty}</span>
            <button onClick={() => setQty((q) => q + 1)} className="p-2" data-testid={`qty-inc-${idKey}`} disabled={out}><Plus size={14} /></button>
          </div>
        </div>

        <Button onClick={buyNow} disabled={out} data-testid={`buy-now-button-${idKey}`} className="w-full bg-forest hover:bg-forest-dark text-cream rounded-full h-11 font-semibold">
          <Zap size={16} className="mr-1.5" /> Buy Now
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={add} disabled={out} variant="outline" data-testid={`add-to-cart-button-${idKey}`} className="border-forest/30 text-forest hover:bg-forest/5 rounded-full h-10 font-semibold">
            <ShoppingCart size={15} className="mr-1" /> Add
          </Button>
          <Button onClick={() => openWhatsApp({ items: [{ product_id: product.id, sku: variant.sku, quantity: qty }] })} variant="outline" data-testid={`whatsapp-button-${idKey}`} className="border-whatsapp/40 text-whatsapp hover:bg-whatsapp/10 rounded-full h-10 font-semibold">
            <MessageCircle size={15} className="mr-1" /> WA
          </Button>
        </div>
      </div>
    </div>
  );
}
